/** Permanent display-only loading correction. No persistence or customer identifiers. */
export const PHOTO_TILE_LIMIT = 6;
export const PHOTO_TILE_TIMEOUT_MS = 12_000;
export const photoTileExperimentEnabled = () => import.meta.env.DEV === true && import.meta.env.MODE !== "test";

/** Private release activation only: all galleries, all jobs, no persisted setting. */
export const photoTilePrivateTestEnabled = () => import.meta.env.VITE_PHOTO_TILE_PRIVATE_TEST === "1";
export const photoTileRenderingEnabled = () => true;
/** Production integrity proves the correction is embedded, independently of diagnostics flags. */
export const PHOTO_TILE_STAMP = "TM_BUILD:PHOTO_TILES=1;SCOPE=all-galleries";
(globalThis as { __TM_PHOTO_TILE_STAMP__?: string }).__TM_PHOTO_TILE_STAMP__ = PHOTO_TILE_STAMP;
/** Manifest and compiled literal must agree; release checks reserve private build numbers. */
export const PRIVATE_TEST_STAMP: string = import.meta.env.VITE_PHOTO_TILE_PRIVATE_TEST === "1"
  ? "TM_BUILD:PRIVATE_TEST=1;SCOPE=all-galleries" : "TM_BUILD:PRIVATE_TEST=0";
(globalThis as { __TM_PRIVATE_TEST_STAMP__?: string }).__TM_PRIVATE_TEST_STAMP__ = PRIVATE_TEST_STAMP;

// Counts-only diagnostics handle for the private iPhone proof (read via the
// Web Inspector console). Function reference returning counts — never URLs.
if (photoTilePrivateTestEnabled()) {
  (globalThis as { __photoTileDiagnostics?: typeof getPhotoTileDiagnostics }).__photoTileDiagnostics = getPhotoTileDiagnostics;
}

export type TileEvent = "mounted" | "unmounted" | "visible" | "queued" | "requested" | "loaded" | "decoded" | "decode_failed" | "failed" | "timed_out" | "cancelled";
const counts: Record<TileEvent, number> = { mounted: 0, unmounted: 0, visible: 0, queued: 0, requested: 0, loaded: 0, decoded: 0, decode_failed: 0, failed: 0, timed_out: 0, cancelled: 0 };
let durationTotalMs = 0;
let maxActive = 0;
let active = 0;
let diagnosticTimer: ReturnType<typeof setTimeout> | undefined;
type Waiter = { start: (release: () => void) => void; cancelled: boolean };
const waiters: Waiter[] = [];

/** Counts only: a load or decode acknowledgement is NOT evidence of displayed pixels. */
export function recordTileEvent(event: TileEvent, durationMs = 0) {
  counts[event]++;
  durationTotalMs += Math.max(0, Math.round(durationMs));
  if (photoTileExperimentEnabled() && !diagnosticTimer) {
    diagnosticTimer = setTimeout(() => {
      diagnosticTimer = undefined;
      console.debug("[PhotoTiles]", getPhotoTileDiagnostics());
    }, 2000);
  }
}
export function getPhotoTileDiagnostics() {
  return { ...counts, active, waiting: waiters.filter((w) => !w.cancelled).length, maxActive, durationTotalMs, pixelsDisplayed: "unverified" as const };
}
function pump() {
  while (active < PHOTO_TILE_LIMIT && waiters.length) {
    const waiter = waiters.shift();
    if (!waiter || waiter.cancelled) continue;
    active++;
    maxActive = Math.max(maxActive, active);
    let released = false;
    waiter.start(() => {
      if (released) return;
      released = true;
      active--;
      pump();
    });
  }
}
/** A permit spans source assignment, network load and decoding, not merely URL resolution. */
export function schedulePhotoTile(start: Waiter["start"]) {
  const waiter: Waiter = { start, cancelled: false };
  waiters.push(waiter);
  recordTileEvent("queued");
  pump();
  return () => { waiter.cancelled = true; };
}
